import omit from 'lodash/omit';
import cloneDeep from 'lodash/cloneDeep';
import map from 'lodash/map';
import compact from 'lodash/compact';
import find from 'lodash/find';
import each from 'lodash/each';
import get from 'lodash/get';
import set from 'lodash/set';
import { BuildingBlock, BuildingBlockLayouts, StepCopy } from '@pendo/services/BuildingBlocks';
import { http } from '@pendo/http';
import { TaskList } from '@pendo/services/TaskList';
import { getContent, RANK_INCREMENT } from '@/guides/helpers/common.util';
import generateUniqueId from '@/utils/makeid';
import { generateCspContent, splitContent, combineContent, p2StepCssPrefix } from '@/guides/helpers/guide-content';

/**
 * This should eventually use code shared with Designer.
 */
class _BuildingBlockGuidesService {
    copy ({ guide, removeTemplateChildren }) {
        const pollIdMappings = {};
        const automationIdMappings = {};
        let oldToNewStepIdMap = {};
        let guideWithContent;

        return getContent(guide)
            .then((content) => {
                const promiseArray = [];
                guideWithContent = content;
                for (let i = 0; i < guide.steps.length; i++) {
                    promiseArray.push(this.objectId());
                }

                return Promise.all(promiseArray);
            })
            .then((newStepIds) => {
                oldToNewStepIdMap = newStepIds.reduce((map, stepId, index) => {
                    map[guide.steps[index].id] = stepId;

                    return map;
                }, {});
            })
            .then(() => this.copyGuideObject(guideWithContent))
            .then((guideCopy) => {
                const stepsPromises = guide.steps.map((step) => {
                    const existingStepId = oldToNewStepIdMap[step.id];

                    return this.copyStepObject(step, guideCopy.id, existingStepId, oldToNewStepIdMap);
                });
                const { bbJson } = guide.attributes?.badge ?? {};

                return Promise.all(stepsPromises).then((stepCopies) => {
                    guideCopy.steps = stepCopies.map((step, index) => {
                        const { stepCopy, pollIdMapping, automationIdMapping } = step;
                        Object.assign(pollIdMappings, pollIdMapping);
                        Object.assign(automationIdMappings, automationIdMapping);
                        stepCopy.rank = RANK_INCREMENT * (index + 1);

                        if (removeTemplateChildren) {
                            this.removeTaskListTemplateChildren(stepCopy);
                        }

                        return stepCopy;
                    });
                    if (guide.polls) {
                        guideCopy.polls = StepCopy.replaceValues(guide.polls, pollIdMappings, true);
                    }
                    if (guide.automations) {
                        guideCopy.automations = StepCopy.replaceValues(guide.automations, automationIdMappings, true);
                    }
                    if (bbJson) {
                        const badgeIds = StepCopy.getAllIds(guideCopy.attributes.badge.bbJson);
                        const { buildingBlocks } = StepCopy.copyBuildingBlocks(guideCopy.attributes.badge.bbJson, badgeIds);
                        Object.assign(guideCopy.attributes.badge, {
                            bbJson: buildingBlocks,
                            domJson: BuildingBlock.buildingBlocksToDom(buildingBlocks),
                        });
                    }

                    return guideCopy;
                });
            });
    }

    removeTaskListTemplateChildren (step) {
        const blocks = JSON.parse(step.buildingBlocks);
        const taskListBlock = TaskList.getTaskListBlock(blocks);

        if (!taskListBlock) return;

        taskListBlock.templateChildren = [];

        step.buildingBlocks = JSON.stringify(blocks);

        return step;
    }

    copyGuideObject (guideToCopy, guideId) {
        const clearProperties = ['createdAt', 'createdByUser', 'lastUpdatedAt', 'lastUpdatedByUser'];

        const guideCopy = cloneDeep(omit(guideToCopy, clearProperties));

        if (!guideId) {
            return this.objectId().then((id) => {
                const isExpired = guideToCopy.expiresAfter && guideToCopy.expiresAfter < Date.now();
                const schedulingOverrides = isExpired ? { showsAfter: 0, expiresAfter: 0 } : {};

                Object.assign(guideCopy, {
                    state: 'draft',
                    name: `${guideToCopy.name} (copy)`,
                    id,
                    polls: [],
                    ...schedulingOverrides,
                });

                return guideCopy;
            });
        }
        guideCopy.polls = [];
        guideCopy.id = guideId;

        return Promise.resolve(guideCopy);
    }

    copyStepObject (stepToCopy, newGuideId, newStepId, stepMap) {
        const copiedStep = cloneDeep(stepToCopy);
        const containerJSON = BuildingBlock.findElementById(JSON.parse(copiedStep.dom), 'pendo-g-');
        const containerId = containerJSON.props.id;

        const id = newStepId;

        const stepCopy = StepCopy.copyStep(stepToCopy, id, containerId, {}, false, { isGuideCopy: true, stepMap });
        const { buildingBlocks, dom, otherProps, pollIdMapping, automationIdMapping } = stepCopy;
        Object.assign(stepCopy, otherProps, {
            buildingBlocks,
            dom,
            guideId: newGuideId,
            id,
            pollIds: Object.values(pollIdMapping),
            automationIds: Object.values(automationIdMapping),
        });

        return this.copyStepCodeBlock(stepCopy).then(() => {
            return {
                pollIdMapping,
                automationIdMapping,
                stepCopy,
            };
        });
    }

    copyStepCodeBlock (step) {
        const codeBlockPromises = [];
        if (step.contentUrl) {
            const contentPromise = new Promise((resolve) => {
                http.get(step.contentUrl, { withCredentials: false }).then((result) => {
                    step.content = result.data;
                    resolve();
                });
            });
            codeBlockPromises.push(contentPromise);
        }
        if (step.contentUrlJs) {
            const contentJsPromise = new Promise((resolve) => {
                http.get(step.contentUrlJs, { withCredentials: false }).then((result) => {
                    step.contentJs = result.data;
                    resolve();
                });
            });
            codeBlockPromises.push(contentJsPromise);
        }
        if (step.contentUrlCss) {
            const contentCssPromise = new Promise((resolve) => {
                http.get(step.contentUrlCss, { withCredentials: false }).then((result) => {
                    step.contentCss = result.data;
                    resolve();
                });
            });
            codeBlockPromises.push(contentCssPromise);
        }

        return Promise.all(codeBlockPromises).then(() => {
            return this.regenerateCodeBlockContent(step);
        });
    }

    regenerateCodeBlockContent (step) {
        const content = {};

        const oldContent = step.content ? splitContent(step.content) : {};
        Object.assign(content, {
            javascript: oldContent.javascript || '',
            html: oldContent.html || '',
            css: get(step, 'attributes.css', ''),
            cssPrefix: p2StepCssPrefix(step),
        });

        return combineContent(content).then((combinedContent) => {
            step.content = combinedContent;
            Object.assign(step, { ...generateCspContent(step, combinedContent) });

            return step;
        });
    }

    copyStepAndPolls (step, guide) {
        return this.copyStepObject(step, guide.id).then(({ pollIdMapping, stepCopy }) => {
            const polls = [];

            if (guide.polls) {
                const clonedPolls = map(pollIdMapping, (newId, oldId) => {
                    const clonedPoll = cloneDeep(find(guide.polls, { id: oldId }));
                    if (!clonedPoll) return;

                    return Object.assign(clonedPoll, { id: newId });
                });

                polls.push(...compact(clonedPolls));
            }

            return {
                polls,
                step: stepCopy,
            };
        });
    }

    createBaseBuildingBlocksForLayout (...args) {
        return BuildingBlockLayouts.createBaseBuildingBlocksForLayout(...args);
    }

    buildingBlocksToDom (...args) {
        return BuildingBlock.buildingBlocksToDom(...args);
    }

    findBlock (...args) {
        return BuildingBlock.findBlock(...args);
    }

    copyProperties (destination, source) {
        if (source.properties) {
            destination.properties = cloneDeep(source.properties);
        }

        ['labelStyles', 'pseudoStyles', 'domClasses'].forEach((webProperty) => {
            const propertyKey = `web.${webProperty}`;
            const propertyValue = cloneDeep(get(source, propertyKey));
            if (propertyValue) {
                set(destination, propertyKey, propertyValue);
            }
        });

        each(destination.views, (destinationView, i) => {
            const sourceView = get(source, `views[${i}]`);
            if (!sourceView) return;
            this.copyProperties(destinationView, sourceView);
        });

        return destination;
    }

    async objectId () {
        const response = await generateUniqueId();

        return response.data.id;
    }
}

export default new _BuildingBlockGuidesService();
